import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { EnvelopeIcon, ExclamationTriangleIcon, PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface SendEmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetUser: {
    id: string;
    name: string;
    surname: string;
    email: string;
  };
}

interface EmailFormData {
  subject: string;
  title: string;
  message: string;
}

const SendEmailDialog: React.FC<SendEmailDialogProps> = ({ 
  isOpen, 
  onClose, 
  targetUser 
}) => {
  const [formData, setFormData] = useState<EmailFormData>({
    subject: '',
    title: '',
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const sendAdminEmail = httpsCallable(functions, 'sendAdminEmailToUser');

  const handleInputChange = (field: keyof EmailFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSend = async () => {
    if (!formData.subject.trim() || !formData.title.trim() || !formData.message.trim()) {
      setResult({
        success: false,
        message: 'Subject, title, and message are all required',
      });
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const response = await sendAdminEmail({
        targetUserId: targetUser.id,
        emailDetails: {
          subject: formData.subject.trim(),
          title: formData.title.trim(),
          message: formData.message.trim(),
        },
      });

      const data = response.data as { success: boolean; message: string };

      setResult({
        success: data.success,
        message: data.message,
      });

      if (data.success) {
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error sending email:', error);
      setResult({
        success: false,
        message: error.message || 'Failed to send email',
      });
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setFormData({
      subject: '',
      title: '',
      message: '',
    });
    setResult(null);
    onClose();
  };

  const predefinedMessages = [
    {
      subject: 'PEAK^ Log Reminder',
      title: 'Time to log with PEAK^!',
      message: "Don't forget to log your daily activities & wellness to stay on track to your peak!",
    },
    {
      subject: 'Welcome to PEAK^',
      title: 'Welcome aboard!',
      message: "We're excited to have you on PEAK^. Start logging your daily activities to track your progress.",
    },
    {
      subject: 'PEAK^ Feature Update',
      title: 'New features are here!',
      message: "We've added new features to help improve your experience. Check them out in the app!",
    },
    {
      subject: 'PEAK^ Support',
      title: 'We\'re here to help',
      message: 'Our support team is here to help if you have any questions. Reach out anytime!',
    },
  ];

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-2">
            <EnvelopeIcon className="w-6 h-6 text-secondary" />
            <h3 className="text-lg font-semibold">Send Email Notification</h3>
          </div>
          <button 
            className="btn btn-sm btn-circle btn-ghost"
            onClick={handleClose}
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Target User Info */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Sending to:</h4>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-secondary text-white flex items-center justify-center text-lg font-semibold">
              {targetUser.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-medium">{targetUser.name} {targetUser.surname}</div>
              <div className="text-sm text-gray-600">{targetUser.email}</div>
              <div className="text-xs text-gray-500">User ID: {targetUser.id}</div>
            </div>
          </div>
        </div>

        {/* Quick Templates */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Templates:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {predefinedMessages.map((template, index) => (
              <button
                key={index}
                className="text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                onClick={() => setFormData({ ...template })}
              >
                <div className="font-medium text-sm">{template.subject}</div>
                <div className="text-xs text-gray-600 line-clamp-2">{template.message}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="space-y-4 mb-6">
          {/* Subject */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Subject *</span>
              <span className="label-text-alt">{formData.subject.length}/100</span>
            </label>
            <input
              type="text"
              placeholder="Enter email subject..."
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              maxLength={100}
              className="input input-bordered w-full"
            />
          </div>

          {/* Title */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Title *</span>
              <span className="label-text-alt">{formData.title.length}/100</span>
            </label>
            <input
              type="text"
              placeholder="Enter email title (headline in the email body)..."
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              maxLength={100}
              className="input input-bordered w-full"
            />
            <label className="label">
              <span className="label-text-alt">The bold headline inside the email</span>
            </label>
          </div>

          {/* Message */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Message *</span>
              <span className="label-text-alt">{formData.message.length}/500</span>
            </label>
            <textarea
              placeholder="Enter email message..."
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              maxLength={500}
              rows={4}
              className="textarea textarea-bordered w-full"
            />
          </div>
        </div>

        {/* Result Display */}
        {result && (
          <div className={`alert mb-6 ${result.success ? 'alert-success' : 'alert-error'}`}>
            {result.success ? (
              <EnvelopeIcon className="w-5 h-5" />
            ) : (
              <ExclamationTriangleIcon className="w-5 h-5" />
            )}
            <div>
              <div className="font-medium">{result.message}</div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="modal-action">
          <button 
            className="btn btn-ghost"
            onClick={handleClose}
            disabled={sending}
          >
            Cancel
          </button>
          <button 
            className="btn btn-secondary"
            onClick={handleSend}
            disabled={sending || !formData.subject.trim() || !formData.title.trim() || !formData.message.trim()}
          >
            {sending ? (
              <>
                <span className="loading loading-spinner loading-sm mr-2"></span>
                Sending...
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                Send Email
              </>
            )}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={handleClose}></div>
    </div>
  );
};

export default SendEmailDialog;
